import React, { useRef } from 'react';
import { motion, MotionValue, useScroll, useTransform } from 'framer-motion';
import { Code, Globe, Layout, Palette } from 'lucide-react';
interface Service {
  icon: ReactNode;
  title: string;
  description: string;
}
interface OrbitCardProps {
  service: Service;
  index: number;
  total: number;
  rotation: MotionValue<number>;
}
interface MobileCardProps {
  service: Service;
  index: number;
}
function WireframeGlobe() {
  return <div className="relative flex h-64 w-64 items-center justify-center md:h-80 md:w-80" data-id="element-300">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 40,
      repeat: Infinity,
      ease: 'linear'
    }} className="relative h-full w-full rounded-full border-2 border-gold/30 shadow-[inset_0_0_50px_rgba(4,107,210,0.2)]" data-id="element-301">
        <div className="absolute h-[30%] w-full rounded-[50%] border border-gold/20" data-id="element-302" />
        <div className="absolute h-[70%] w-full rounded-[50%] border border-gold/20" data-id="element-303" />
        <div className="absolute h-full w-[30%] rounded-[50%] border border-gold/20" data-id="element-304" />
        <div className="absolute h-full w-[70%] rounded-[50%] border border-gold/20" data-id="element-305" />
        <div className="absolute h-full w-[15%] rounded-[50%] border border-gold/10" data-id="element-306" />
        <div className="absolute h-full w-[85%] rounded-[50%] border border-gold/10" data-id="element-307" />
      </motion.div>
      <div className="absolute h-3 w-3 rounded-full bg-gold shadow-[0_0_22px_rgba(4,107,210,0.9)]" data-id="element-308" />
    </div>;
}
function OrbitCard({
  service,
  index,
  total,
  rotation
}: OrbitCardProps) {
  const baseAngle = index / total * 360;
  const x = useTransform(rotation, value => {
    const angle = (baseAngle + value) * (Math.PI / 180);
    return Math.sin(angle) * 380;
  });
  const y = useTransform(rotation, value => {
    const angle = (baseAngle + value) * (Math.PI / 180);
    return Math.cos(angle) * 120;
  });
  const z = useTransform(rotation, value => {
    const angle = (baseAngle + value) * (Math.PI / 180);
    return Math.cos(angle);
  });
  const scale = useTransform(z, [-1, 1], [0.65, 1.1]);
  const opacity = useTransform(z, [-1, 1], [0.3, 1]);
  const zIndex = useTransform(z, value => Math.round(value * 50) + 50);
  return <motion.div style={{
    x,
    y,
    scale,
    opacity,
    zIndex,
    position: 'absolute'
  }} className="w-[280px]" data-id="element-309">
      <motion.div animate={{
      y: [-5, 5, -5]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: index * 0.5
    }} className="rounded-2xl border border-white/10 bg-dark-lighter/90 p-6 shadow-xl backdrop-blur-md" data-id="element-310">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/5 bg-dark text-gold" data-id="element-311">
          {service.icon}
        </div>
        <h3 className="font-syne text-xl font-bold text-white" data-id="element-312">
          {service.title}
        </h3>
        <p className="mt-3 font-inter text-sm leading-relaxed text-white/60" data-id="element-313">
          {service.description}
        </p>
      </motion.div>
    </motion.div>;
}
function MobileCard({
  service,
  index
}: MobileCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="rounded-2xl border border-white/5 bg-dark-lighter p-8" data-id="element-314">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-white/5 bg-dark text-gold" data-id="element-315">
        {service.icon}
      </div>
      <h3 className="font-syne text-xl font-bold text-white" data-id="element-316">
        {service.title}
      </h3>
      <p className="mt-4 font-inter text-sm leading-relaxed text-white/60" data-id="element-317">
        {service.description}
      </p>
    </motion.div>;
}
export function ServicesSection() {
  const services: Service[] = [{
    icon: <Palette className="h-8 w-8" data-id="element-318" />,
    title: 'Digital Experience Design',
    description: 'Clear, compelling interfaces shaped around customer needs, business goals and the moments that matter most.'
  }, {
    icon: <Layout className="h-8 w-8" data-id="element-319" />,
    title: 'Content Management Platforms',
    description: 'Thoughtful WordPress and Drupal solutions that give teams the confidence to manage and evolve their digital presence.'
  }, {
    icon: <Code className="h-8 w-8" data-id="element-320" />,
    title: 'Web Development',
    description: 'Responsive, resilient websites and platforms built with performance, accessibility and long-term maintainability in mind.'
  }, {
    icon: <Globe className="h-8 w-8" data-id="element-321" />,
    title: 'E-commerce & Growth',
    description: 'Storefronts and conversion journeys that make it easier for customers to browse, decide and return.'
  }];
  const containerRef = useRef<HTMLElement | null>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  const rotation = useTransform(scrollYProgress, [0, 1], [0, 360]);
  return <section id="services" ref={containerRef} className="relative bg-dark md:h-[250vh]" data-id="element-322">
      <div className="flex w-full flex-col items-center overflow-hidden py-24 md:sticky md:top-0 md:h-screen md:justify-center md:py-0" data-id="element-323">
        <div className="z-50 mb-16 w-full px-4 text-center md:absolute md:left-0 md:top-24 md:mb-0" data-id="element-324">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-3 font-inter text-xs font-bold uppercase tracking-[0.2em] text-gold" data-id="element-325">
            Capabilities
          </motion.p>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-syne text-4xl font-bold text-white md:text-5xl" data-id="element-326">
            One team. <span className="text-gold" data-id="element-327">Every critical layer.</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="mx-auto mt-4 max-w-2xl font-inter text-white/60" data-id="element-328">
            We bring strategy, design and delivery into the same conversation so
            your digital investment moves with purpose.
          </motion.p>
        </div>

        <div className="relative mt-20 hidden h-[600px] w-full max-w-[1000px] items-center justify-center md:flex" data-id="element-329">
          <WireframeGlobe data-id="element-330" />
          {services.map((service, index) => <OrbitCard key={service.title} service={service} index={index} total={services.length} rotation={rotation} data-id="element-331" />)}
        </div>

        <div className="w-full max-w-md space-y-6 px-4 md:hidden" data-id="element-332">
          <div className="mb-12 flex justify-center scale-90" data-id="element-333">
            <WireframeGlobe data-id="element-334" />
          </div>
          {services.map((service, index) => <MobileCard key={service.title} service={service} index={index} data-id="element-335" />)}
        </div>
      </div>
    </section>;
}